import { MouseEvent } from 'react';

import { isProd } from 'utils/env';

import { isEmitterInitialized, track } from './segmentEmitter';

export interface TrackedLinkClickHandlerUtilOptions {
  /**
   * Name of the event being sent to Segment on link click
   */
  eventName: string;
  /**
   * Returns a map of properties to be sent as a payload to Segment. Receives the
   * arguments that were passed to the click handler function returned by
   * `createTrackedLinkClickHandler`.
   */
  getEventProperties: (
    linkClickEvent: MouseEvent<HTMLAnchorElement>,
    ...clickHandlerArgs: any[]
  ) => Record<string, unknown>;
}

/**
 * Returns a click handler function for tracking link clicks with Segment. Pass
 * the result of this function to an anchor tag's `onClick` prop to enable tracking.
 *
 * Note: This function is based on Segment's `trackLink` SDK method. Segment's
 * `trackLink` method is not compatible with React because it uses the browser's
 * event loop instead of React's synthetic event handling system, and because
 * Segment does not provide a way to clean up event listeners when tracked React
 * components unmount. This function reimplements `trackLink` and provides React-
 * friendly `onClick` handler functions instead.
 *
 * More info:
 * https://github.com/segmentio/analytics.js/blob/7dda4259d40375666ea6fd166d51b208d79c38ec/analytics.js#L495-L522
 */
export const createTrackedLinkClickHandler =
  ({ eventName, getEventProperties }: TrackedLinkClickHandlerUtilOptions) =>
  (linkClickEvent: MouseEvent<HTMLAnchorElement>, ...clickHandlerArgs: any[]) => {
    const linkElement = linkClickEvent.currentTarget;

    // Determine if the current frame will redirect (based on keys pressed and link target attribute)
    const isMetaKeyPressed =
      linkClickEvent.metaKey ||
      linkClickEvent.altKey ||
      linkClickEvent.ctrlKey ||
      linkClickEvent.shiftKey;
    const isBlankLinkTarget = linkElement.target === '_blank';
    const currentFrameWillRedirect = !isMetaKeyPressed && !isBlankLinkTarget;

    let segmentCallback = () => {
      // no-op
    };

    if (
      linkElement.href &&
      currentFrameWillRedirect &&
      // Safety check: Non-initialized emitter will not execute `segmentCallback`. Skip callback logic if Segment isn't initialized.
      isEmitterInitialized()
    ) {
      // Prevent the current frame from redirecting to prevent Segment events from being dropped
      linkClickEvent.preventDefault();
      linkClickEvent.stopPropagation();

      // Redirect the current frame after Segment tracks the click
      segmentCallback = () => window.location.assign(linkElement.href);
    }

    // Track the click with Segment
    try {
      track(
        eventName,
        getEventProperties(linkClickEvent, ...clickHandlerArgs),
        {},
        segmentCallback,
      );
    } catch (error) {
      if (isProd()) {
        console.error(error);
      }

      /**
       * If `emitter.track` throws, we don't want to block navigation for the user.
       * So, this calls `segmentCallback` in case we're relying on it to perform
       * the redirect.
       */
      segmentCallback();
    }
  };
